import { useState, useRef, useCallback } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Build absolute URL from a stored path like `/api/uploads/xxx_full.webp`.
 */
function abs(url) {
  if (!url) return "";
  return url.startsWith("/api") ? `${BACKEND_URL}${url}` : url;
}

/**
 * Given a base path like `/api/uploads/xxx_full.webp`, return the same path
 * with a different variant suffix (_full / _med / _thumb).
 */
function withVariant(url, variant) {
  if (!url || !url.startsWith("/api")) return url;
  return url.replace(/(_full|_med|_thumb)\.webp$/, `_${variant}.webp`);
}

/**
 * <SmartImage>
 * Production-grade reliable image component:
 *  - srcset for responsive sizes (mobile gets smaller variant)
 *  - eager + fetchpriority for above-the-fold main images
 *  - lazy + fade-in skeleton for below-the-fold gallery
 *  - retry once on error (cache-bust), then fallback through variants
 *
 * Props:
 *   src        – /api/uploads/xxx_full.webp (or any variant)
 *   variant    – "full" | "med" | "thumb"  (which size to render as primary)
 *   priority   – boolean, true => eager + fetchpriority=high + decoding=sync
 *   srcset     – boolean, true => provide responsive srcset (mobile + desktop)
 *   alt        – alt text
 *   className  – Tailwind classes
 *   sizes      – CSS sizes attribute (default "100vw")
 *   testId     – data-testid
 *   onLoad     – callback (e) when first load completes
 */
export function SmartImage({
  src,
  variant = "med",
  priority = false,
  srcset = false,
  alt = "",
  className = "",
  sizes = "100vw",
  testId,
  onLoad,
  ...rest
}) {
  const [loaded, setLoaded] = useState(false);
  const retried = useRef(false);
  const triedSources = useRef([]);
  const placeholderClasses = "absolute inset-0 bg-gradient-to-br from-white/[0.04] to-white/[0.02] animate-pulse rounded";

  // Primary src: variant indicated by prop
  const primary = withVariant(src, variant);

  // Build srcset for responsive loading
  const srcSetAttr = (srcset && src && src.startsWith("/api"))
    ? `${abs(withVariant(src, "thumb"))} 240w, ${abs(withVariant(src, "med"))} 1000w, ${abs(withVariant(src, "full"))} 1600w`
    : undefined;

  const handleError = useCallback((e) => {
    const img = e.currentTarget;
    const currentSrc = img.src;

    // Retry once with cache-bust (transient network/CDN issues)
    if (!retried.current) {
      retried.current = true;
      triedSources.current.push(currentSrc);
      const url = new URL(currentSrc);
      url.searchParams.set("_r", String(Date.now()));
      img.src = url.toString();
      return;
    }

    // After retry: walk through fallback chain (other variants + original exts)
    if (src && src.startsWith("/api")) {
      const candidates = [
        abs(withVariant(src, "full")),
        abs(withVariant(src, "med")),
        abs(withVariant(src, "thumb")),
        abs(src.replace(/(_full|_med|_thumb)\.webp$/, ".jpeg")),
        abs(src.replace(/(_full|_med|_thumb)\.webp$/, ".jpg")),
        abs(src.replace(/(_full|_med|_thumb)\.webp$/, ".png")),
        abs(src),
      ];
      const next = candidates.find(u => u && !triedSources.current.includes(u) && u !== currentSrc);
      if (next) {
        triedSources.current.push(currentSrc);
        img.src = next;
        return;
      }
    }

    // All attempts failed → show placeholder by clearing src & marking loaded
    img.onerror = null;
    setLoaded(true);
    img.style.opacity = "0";
  }, [src]);

  return (
    <span className={`relative block ${className}`} data-testid={testId ? `${testId}-wrapper` : undefined}>
      {!loaded && <span className={placeholderClasses} aria-hidden="true" />}
      <img
        src={abs(primary)}
        srcSet={srcSetAttr}
        sizes={srcSetAttr ? sizes : undefined}
        alt={alt}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "low"}
        decoding={priority ? "sync" : "async"}
        className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}
        onLoad={(e) => { setLoaded(true); onLoad && onLoad(e); }}
        onError={handleError}
        data-testid={testId}
        {...rest}
      />
    </span>
  );
}

/**
 * Preload helper - call from page-level useEffect to start downloading
 * the main image before the component mounts.
 */
export function preloadImage(url, variant = "med") {
  if (!url) return;
  const path = withVariant(url, variant);
  const finalUrl = abs(path);
  if (typeof document === "undefined") return;
  // Skip if already preloaded
  if (document.head.querySelector(`link[rel="preload"][href="${finalUrl}"]`)) return;
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "image";
  link.href = finalUrl;
  link.fetchPriority = "high";
  document.head.appendChild(link);
}
